import React, { useEffect } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import {
  CalendarIcon,
  CheckIcon,
  SelectorIcon,
} from "@heroicons/react/outline";
import { classNames } from "../../Utils";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import {
  requestListBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import ReactDatePicker from "react-datepicker";
import { useLocation } from "react-router-dom";
import { dateFilterOptions } from "../../Constants";
import "./style.css";

export const TransactionFilter = ({
  selectedBusiness,
  setSelectedBusiness,
  date,
  setDate,
  dateRange,
  setDateRange,
  startDate,
  endDate,
  selectedFilter,
  setSelectedFilter,
  createdAt,
  selectedMonth,
  setselectedMonth,
  selectedYear,
  setselectedYear,
  months,
  years,
  onLoad,
  showAdditionalButton = false,
  onClick,
  btnDisabled,
  additionalButtonTitle,
  loading,
  filterDate = "period" || "date" || "range",
  children,
  additionalButtonColor,
  customDateFilterOptions = dateFilterOptions,
  CustomFilterComponent,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  const { user } = useAuthState();

  const dispatch = useBusinessTransactionDispatch();

  const { businesses } = useBusinessTransactionState();

  useEffect(() => {
    if (!businesses.length && user.id) {
      requestListBusiness(dispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [dispatch, businesses.length, user.id]);

  useEffect(() => {
    if (businessIdParam && businesses.length) {
      const business = businesses.find(
        (item) => item.value === businessIdParam
      );
      if (business) {
        setSelectedBusiness({
          ...business,
          label: business.name,
          value: business.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessIdParam, businesses]);

  useEffect(() => {
    if (
      selectedFilter?.value !== "OTHER" ||
      (dateRange && dateRange[0] && dateRange[1])
    ) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAt]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  return (
    <div className="flex flex-col flex-wrap sm:flex-row sm:items-center sm:w-full justify-between gap-4">
      <div className="flex flex-col flex-wrap sm:flex-row gap-4">
        <div>
          <Listbox value={selectedBusiness} onChange={setSelectedBusiness}>
            {({ open }) => (
              <>
                <ListboxButton
                  className={classNames(
                    businesses.length === 0
                      ? "bg-gray-200 border-transparent"
                      : "bg-white",
                    "flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md shadow-sm sm:text-sm"
                  )}
                >
                  <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                    {selectedBusiness?.label || t("Business Name")}
                  </span>
                  {businesses.length === 0 ? (
                    <svg
                      role="status"
                      className="w-5 h-5 animate-spin text-white"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#E5E7EB"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <SelectorIcon className="w-5 h-5 text-gray-500" />
                  )}
                </ListboxButton>
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm sm:w-auto">
                    {businesses.map((business) => (
                      <ListboxOption
                        key={business.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={business}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {business.label}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        {CustomFilterComponent}
        {/* Day */}
        {filterDate === "date" ? (
          <div className="relative flex">
            <ReactDatePicker
              className="block pl-6 w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 cursor-pointer"
              selected={date}
              onChange={setDate}
              placeholderText={t("Select Date")}
              dateFormat="dd/MM/yyyy"
            />
            <CalendarIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black" />
          </div>
        ) : (
          false
        )}
        {filterDate === "period" ? (
          <>
            {/* month */}
            <div>
              <Listbox value={selectedMonth} onChange={setselectedMonth}>
                {({ open }) => (
                  <>
                    <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                      <span className="ml-3 mr-2">
                        {t(selectedMonth?.label || "Month")}
                      </span>
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    </ListboxButton>
                    <Transition
                      show={open}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                        {months.map((month) => (
                          <ListboxOption
                            key={month.value}
                            className={({ focus }) =>
                              classNames(
                                focus
                                  ? "text-white bg-blue-600"
                                  : "text-gray-900",
                                "cursor-default select-none relative py-2 pl-3 pr-9"
                              )
                            }
                            value={month}
                          >
                            {({ selected, focus }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span
                                    className={classNames(
                                      selected
                                        ? "font-semibold"
                                        : "font-normal",
                                      "ml-3 mr-2 block truncate"
                                    )}
                                  >
                                    {t(month.label)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        focus ? "text-white" : "text-blue-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
            {/* year */}
            <div>
              <Listbox value={selectedYear} onChange={setselectedYear}>
                {({ open }) => (
                  <>
                    <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                      <span className="ml-3 mr-2">
                        {t(selectedYear?.label || "Year")}
                      </span>
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    </ListboxButton>
                    <Transition
                      show={open}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                        {years.map((year) => (
                          <ListboxOption
                            key={year.value}
                            className={({ focus }) =>
                              classNames(
                                focus
                                  ? "text-white bg-blue-600"
                                  : "text-gray-900",
                                "cursor-default select-none relative py-2 pl-3 pr-9"
                              )
                            }
                            value={year}
                          >
                            {({ selected, focus }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span
                                    className={classNames(
                                      selected
                                        ? "font-semibold"
                                        : "font-normal",
                                      "ml-3 mr-2 block truncate"
                                    )}
                                  >
                                    {t(year.label)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        focus ? "text-white" : "text-blue-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
          </>
        ) : (
          false
        )}
        {filterDate === "range" && (
          <>
            <div>
              <Listbox value={selectedFilter} onChange={setSelectedFilter}>
                {({ open }) => (
                  <>
                    <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                      <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                        {t(selectedFilter?.label || "Today")}
                      </span>
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    </ListboxButton>
                    <Transition
                      show={open}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                        {customDateFilterOptions.map((filter) => (
                          <ListboxOption
                            key={filter.value}
                            className={({ focus }) =>
                              classNames(
                                focus
                                  ? "text-white bg-blue-600"
                                  : "text-gray-900",
                                "cursor-default select-none relative py-2 pl-3 pr-9"
                              )
                            }
                            value={filter}
                          >
                            {({ selected, focus }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span
                                    className={classNames(
                                      selected
                                        ? "font-semibold"
                                        : "font-normal",
                                      "ml-3 mr-2 block truncate"
                                    )}
                                  >
                                    {t(filter.label)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        focus ? "text-white" : "text-blue-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
            {selectedFilter?.value === "MONTHLY" && (
              <div>
                <ReactDatePicker
                  className="block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 cursor-pointer pl-6 pr-3 disabled:bg-gray-100"
                  selected={date}
                  showMonthYearPicker
                  dateFormat="MM/yyyy"
                  onChange={setDate}
                />
              </div>
            )}
            {selectedFilter?.value === "YEARLY" && (
              <div>
                <ReactDatePicker
                  className="block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 cursor-pointer pl-6 pr-3 disabled:bg-gray-100"
                  selected={date}
                  showYearPicker
                  dateFormat="yyyy"
                  onChange={setDate}
                />
              </div>
            )}
            {selectedFilter?.value === "OTHER" && (
              <div>
                <ReactDatePicker
                  className="block w-full sm:w-52 text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 cursor-pointer pl-6 disabled:bg-gray-100"
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  maxDate={(() => {
                    const maxEndDate = new Date(startDate);
                    maxEndDate.setMonth(maxEndDate.getMonth() + 3);
                    return maxEndDate;
                  })()}
                  onChange={(update) => {
                    setDateRange(update);
                  }}
                  placeholderText={t("Select Date Range")}
                />
              </div>
            )}
          </>
        )}
        {showAdditionalButton && (
          <div className="flex gap-4 justify-around">
            <div className="w-full justify-start sm:justify-end">
              <Button
                color={additionalButtonColor}
                className="w-full"
                type="button"
                onClick={onClick}
                disabled={btnDisabled}
                title={t(additionalButtonTitle)}
                loading={loading}
              />
            </div>
          </div>
        )}
      </div>
      {children}
    </div>
  );
};
